import React, { useState, useEffect, useMemo } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { FloatingFAB } from '../components/dashboard/FloatingFAB';

const API = process.env.REACT_APP_BACKEND_URL || '';

const GUIDE = {
  sections: [
    { title: 'Period', desc: 'Quick presets (1M/3M/6M/1Y/YTD/All Time) or pick an exact custom date range — both restrict the chart and summary table to that window.' },
    { title: 'Grade / Count', desc: 'Switch between an individual grade or the averaged view across all grades.' },
    { title: 'Supplier view', desc: 'Show all three suppliers together, or isolate one to reduce clutter on the chart.' },
    { title: 'Summary table', desc: 'Average, min, max and latest rate for the selected period, plus how the latest price compares to the period average.' },
  ],
  lookFor: 'A latest price well above the period average (red trend) signals the supplier is currently paying/quoting above their own recent norm.',
};

const SUPPLIERS = [
  { key: 'SIL', label: 'SIL', color: 'var(--dash-blue)', badgeBg: 'rgba(69,170,242,.15)' },
  { key: 'AquaBrahma', label: 'Aqua Brahma', color: 'var(--dash-amber)', badgeBg: 'rgba(242,177,76,.15)' },
  { key: 'SKM', label: 'SKM', color: 'var(--dash-coral)', badgeBg: 'rgba(232,115,74,.15)' },
];

const PERIOD_OPTIONS = [
  { id: 'ALL', label: 'All Time' },
  { id: '1M', label: '1M' },
  { id: '3M', label: '3M' },
  { id: '6M', label: '6M' },
  { id: '1Y', label: '1Y' },
  { id: 'YTD', label: 'YTD' },
];

/* Resolves a period preset (or a custom date range) to a [startIndex, endIndex)
   slice over a grade's own chronological `dates` array — ported from the legacy
   price-trend1.html's setPeriod()/applyCustomDates(). */
const computeRangeIndices = (dates, period, customStart, customEnd) => {
  const total = dates.length;
  if (!total) return [0, 0];
  if (period === 'CUSTOM') {
    if (!customStart && !customEnd) return [0, total];
    let s = customStart ? dates.findIndex(d => d >= customStart) : 0;
    let eIdx = total - 1;
    if (customEnd) {
      eIdx = -1;
      for (let i = total - 1; i >= 0; i--) { if (dates[i] <= customEnd) { eIdx = i; break; } }
    }
    if (s === -1) s = 0;
    if (eIdx < 0) eIdx = total - 1;
    return [s, eIdx + 1];
  }
  const maxDateStr = dates[total - 1];
  const maxDate = new Date(maxDateStr);
  let startIndex = 0;
  if (period === '1M') { const t = new Date(maxDate); t.setMonth(t.getMonth() - 1); startIndex = dates.findIndex(d => new Date(d) >= t); }
  else if (period === '3M') { const t = new Date(maxDate); t.setMonth(t.getMonth() - 3); startIndex = dates.findIndex(d => new Date(d) >= t); }
  else if (period === '6M') { const t = new Date(maxDate); t.setMonth(t.getMonth() - 6); startIndex = dates.findIndex(d => new Date(d) >= t); }
  else if (period === '1Y') { const t = new Date(maxDate); t.setFullYear(t.getFullYear() - 1); startIndex = dates.findIndex(d => new Date(d) >= t); }
  else if (period === 'YTD') { startIndex = dates.findIndex(d => d.startsWith(String(maxDate.getFullYear()))); }
  if (startIndex === -1) startIndex = 0;
  return [startIndex, total];
};

const countToNum = (c) => { const m = String(c).match(/\d+/); return m ? +m[0] : 999; };
const fmtNum = (n) => (n == null || isNaN(n)) ? null : (+n).toFixed(2);

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
// The source data has a handful of corrupted date entries (e.g. two dates
// jammed into one cell: "17-09-2023 & 18-09-2023"). Those sort before every
// real ISO date lexicographically and <input type="date"> silently rejects
// them, so strip any row whose date isn't a clean yyyy-mm-dd before use.
const sanitizeSeries = (obj) => {
  const dates = obj.dates || [];
  if (dates.every(d => ISO_DATE_RE.test(d))) return obj;
  const keepIdx = [];
  dates.forEach((d, i) => { if (ISO_DATE_RE.test(d)) keepIdx.push(i); });
  const pick = (arr) => keepIdx.map(i => (arr || [])[i]);
  return { dates: pick(dates), SIL: pick(obj.SIL), AquaBrahma: pick(obj.AquaBrahma), SKM: pick(obj.SKM) };
};

/* Averages all grades together into a synthetic "ALL_GRADES" series — ported from
   the legacy price-trend1.html's computeAllGradesAverage(). */
const computeAllGradesAverage = (gd) => {
  const dateSet = new Set();
  Object.values(gd).forEach(g => (g.dates || []).forEach(d => dateSet.add(d)));
  const allDates = [...dateSet].sort();
  const suppliers = ['SIL', 'AquaBrahma', 'SKM'];
  const result = { dates: allDates };
  suppliers.forEach(s => { result[s] = []; });
  const gradeLookup = {};
  Object.entries(gd).forEach(([grade, g]) => {
    const m = {};
    (g.dates || []).forEach((d, i) => {
      m[d] = { SIL: g.SIL ? g.SIL[i] : null, AquaBrahma: g.AquaBrahma ? g.AquaBrahma[i] : null, SKM: g.SKM ? g.SKM[i] : null };
    });
    gradeLookup[grade] = m;
  });
  allDates.forEach((d) => {
    suppliers.forEach((s) => {
      const vals = [];
      Object.values(gradeLookup).forEach((m) => {
        const rec = m[d];
        if (rec && rec[s] != null && !isNaN(rec[s]) && rec[s] > 0) vals.push(rec[s]);
      });
      result[s].push(vals.length ? +(vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2) : null);
    });
  });
  return result;
};

const calcStats = (arr) => {
  const valid = arr.filter(v => v !== null && v !== undefined && !isNaN(v));
  if (!valid.length) return null;
  const sum = valid.reduce((a, b) => a + b, 0);
  return {
    avg: sum / valid.length,
    min: Math.min(...valid),
    max: Math.max(...valid),
    latest: valid[valid.length - 1],
  };
};

/* ── Skeleton (loading state) — mirrors this page's header/controls/chart/table
   shape, same shimmer-bone convention as DashboardSkeleton.jsx / InvoicePISkeleton ── */
const SB = ({ className = '', style, delay = 0 }) => (
  <div className={`skel-bone skel-pill ${className}`} style={{ ...style, animationDelay: `${delay}ms` }} />
);

const PriceTrendSkeleton = () => (
  <div className="min-h-full bg-dash-bg text-dash-ink font-grotesk antialiased" data-testid="price-trend-skeleton">
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">

      {/* Header */}
      <div className="flex items-center gap-3 bg-dash-panel p-6 rounded-xl border border-dash-line shadow-sm">
        <div className="skel-bone" style={{ width: 36, height: 36, borderRadius: 8, animationDelay: '0ms' }} />
        <div>
          <SB className="h-4 w-56 mb-2" delay={20} />
          <SB className="h-2.5 w-80" delay={40} />
        </div>
      </div>

      {/* Controls: grade, supplier, period presets, custom range */}
      <div className="bg-dash-panel p-4 rounded-xl border border-dash-line shadow-sm flex flex-wrap items-center gap-3">
        <SB className="h-8 w-48 !rounded-lg" delay={60} />
        <SB className="h-8 w-52 !rounded-lg" delay={80} />
        <div className="flex gap-1">
          {[0, 1, 2, 3, 4, 5].map(i => <SB key={i} className="h-8 w-12 !rounded-md" delay={100 + i * 15} />)}
        </div>
        <SB className="h-8 w-64 !rounded-lg ml-auto" delay={200} />
      </div>

      {/* Chart */}
      <div className="bg-dash-panel border border-dash-line rounded-xl p-5">
        <SB className="h-3.5 w-40 mb-2" delay={240} />
        <SB className="h-2.5 w-64 mb-4" delay={260} />
        <div className="h-[420px] relative overflow-hidden rounded-lg" style={{ background: 'var(--dash-bg)' }}>
          <svg className="absolute inset-0 w-full h-full opacity-40" preserveAspectRatio="none" viewBox="0 0 400 200">
            <polyline
              points="0,150 40,120 80,135 120,90 160,110 200,60 240,80 280,50 320,70 360,40 400,55"
              fill="none" stroke="var(--skel-border)" strokeWidth="3"
            />
          </svg>
        </div>
      </div>

      {/* Summary table */}
      <div className="bg-dash-panel border border-dash-line rounded-xl p-5">
        <SB className="h-3.5 w-56 mb-3" delay={300} />
        <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--skel-border)' }}>
          <div className="px-2.5 py-2.5 flex gap-6 items-center" style={{ background: 'var(--skel-border)' }}>
            {[16, 40, 30, 30, 30, 40].map((w, i) => <SB key={i} className="h-2.5" style={{ width: `${w * 3}px` }} delay={320 + i * 15} />)}
          </div>
          {[0, 1, 2].map(i => (
            <div key={i} className="px-2.5 py-3 flex gap-6 items-center" style={{ borderTop: '1px solid var(--skel-border)' }}>
              <SB className="h-4 w-16 !rounded" delay={400 + i * 40} />
              <SB className="h-3 w-16" delay={410 + i * 40} />
              <SB className="h-3 w-16" delay={420 + i * 40} />
              <SB className="h-3 w-16" delay={430 + i * 40} />
              <SB className="h-3 w-16" delay={440 + i * 40} />
              <SB className="h-3 w-24" delay={450 + i * 40} />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-dash-panel border border-dash-line rounded-lg p-2.5 text-[11px] shadow-xl min-w-[150px]">
      <div className="font-bold text-dash-ink mb-1">{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} className="flex justify-between gap-4">
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="font-mono text-dash-ink">{p.value == null ? '—' : `₹${(+p.value).toFixed(2)}`}</span>
        </div>
      ))}
    </div>
  );
};

const PriceTrendReact = () => {
  const [gradeData, setGradeData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [gradeSelect, setGradeSelect] = useState('ALL_GRADES');
  const [supplierSelect, setSupplierSelect] = useState('ALL');

  const [period, setPeriod] = useState('ALL');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API}/api/price-trend/data`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.json();
        const data = {};
        Object.entries(raw).forEach(([g, obj]) => { data[g] = sanitizeSeries(obj); });
        data['ALL_GRADES'] = computeAllGradesAverage(data);
        setGradeData(data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const grades = useMemo(
    () => Object.keys(gradeData).filter(g => g !== 'ALL_GRADES').sort((a, b) => countToNum(a) - countToNum(b)),
    [gradeData]
  );

  const activeData = useMemo(
    () => gradeData[gradeSelect] || { dates: [], SIL: [], AquaBrahma: [], SKM: [] },
    [gradeData, gradeSelect]
  );

  const rangeIdx = useMemo(
    () => computeRangeIndices(activeData.dates || [], period, customStart, customEnd),
    [activeData, period, customStart, customEnd]
  );

  const chartData = useMemo(() => {
    const dates = activeData.dates || [];
    const sil = activeData.SIL || [];
    const aqua = activeData.AquaBrahma || [];
    const skm = activeData.SKM || [];
    const [s, e] = rangeIdx;
    const out = [];
    for (let i = s; i < e; i++) {
      out.push({ date: dates[i], SIL: sil[i] ?? null, AquaBrahma: aqua[i] ?? null, SKM: skm[i] ?? null });
    }
    return out;
  }, [activeData, rangeIdx]);

  // Date-input display: reflects the active preset's resolved range, unless the
  // user is driving a custom range directly.
  const displayStart = period === 'CUSTOM' ? customStart : (activeData.dates?.[rangeIdx[0]] || '');
  const displayEnd = period === 'CUSTOM' ? customEnd : (activeData.dates?.[Math.max(0, rangeIdx[1] - 1)] || '');
  const dateBounds = { min: activeData.dates?.[0] || undefined, max: activeData.dates?.[activeData.dates.length - 1] || undefined };

  const stats = useMemo(() => ({
    SIL: calcStats(chartData.map(r => r.SIL)),
    AquaBrahma: calcStats(chartData.map(r => r.AquaBrahma)),
    SKM: calcStats(chartData.map(r => r.SKM)),
  }), [chartData]);

  const visibleSuppliers = SUPPLIERS.filter(s => supplierSelect === 'ALL' || supplierSelect === (s.key === 'AquaBrahma' ? 'AQUA' : s.key));

  const gradeLabel = gradeSelect === 'ALL_GRADES' ? 'All Grades' : `Grade ${gradeSelect}`;

  // Y-axis range: tight to the visible series (highest value + a small buffer),
  // instead of Recharts' default "nice round number" auto-scale which over-pads.
  const yDomain = useMemo(() => {
    const vals = [];
    chartData.forEach((r) => {
      visibleSuppliers.forEach((s) => { const v = r[s.key]; if (v != null) vals.push(v); });
    });
    if (!vals.length) return [0, 'auto'];
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const buffer = max >= 100 ? 20 : 10;
    return [Math.max(0, Math.floor(min - buffer)), Math.ceil(max + buffer)];
  }, [chartData, visibleSuppliers]);

  if (loading) return <PriceTrendSkeleton />;

  return (
    <div className="min-h-full bg-dash-bg text-dash-ink font-grotesk antialiased">
      <div className="max-w-[1400px] mx-auto p-6 space-y-6">

        {/* Header */}
        <div className="flex items-center gap-3 bg-dash-panel p-6 rounded-xl border border-dash-line shadow-sm">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center font-grotesk font-extrabold text-sm shrink-0"
            style={{ background: 'linear-gradient(135deg, var(--dash-blue) 0%, var(--dash-green) 100%)', color: '#081519' }}
          >
            P
          </div>
          <div>
            <h1 className="text-lg font-bold text-dash-ink">Raw Material Price Trend</h1>
            <p className="text-[10px] text-dash-mut uppercase tracking-wider">Grade-wise & supplier-specific daily trend intelligence</p>
          </div>
        </div>

        {error && (
          <div className="bg-dash-panel border border-dash-line rounded-xl p-10 text-center text-dash-coral text-sm">Error: {error}</div>
        )}

        {!error && (
          <>
            {/* Controls: grade, supplier, period presets, custom range */}
            <div className="bg-dash-panel p-4 rounded-xl border border-dash-line shadow-sm flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 bg-dash-panel2 border border-dash-line rounded-lg px-3 py-1.5">
                <label className="text-[10px] uppercase tracking-wider text-dash-mut font-semibold">Grade / Count</label>
                <select
                  value={gradeSelect}
                  onChange={(e) => setGradeSelect(e.target.value)}
                  className="bg-dash-panel text-dash-ink text-xs font-semibold rounded px-1.5 py-0.5 focus:outline-none"
                  data-testid="grade-select"
                >
                  <option value="ALL_GRADES">All Grades (Average)</option>
                  {grades.map(g => <option key={g} value={g}>Count / Grade {g}</option>)}
                </select>
              </div>

              <div className="flex items-center gap-2 bg-dash-panel2 border border-dash-line rounded-lg px-3 py-1.5">
                <label className="text-[10px] uppercase tracking-wider text-dash-mut font-semibold">Supplier View</label>
                <select
                  value={supplierSelect}
                  onChange={(e) => setSupplierSelect(e.target.value)}
                  className="bg-dash-panel text-dash-ink text-xs font-semibold rounded px-1.5 py-0.5 focus:outline-none"
                  data-testid="supplier-select"
                >
                  <option value="ALL">All Suppliers (Comparison)</option>
                  <option value="SIL">SIL Only</option>
                  <option value="AQUA">Aqua Brahma Only</option>
                  <option value="SKM">SKM Only</option>
                </select>
              </div>

              <div className="flex gap-1" data-testid="period-buttons">
                {PERIOD_OPTIONS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setPeriod(p.id)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${period === p.id ? 'bg-dash-ice text-white font-semibold' : 'bg-dash-panel2 border border-dash-line text-dash-mut hover:text-dash-ink'}`}
                    data-testid={`period-btn-${p.id}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 bg-dash-panel2 p-1.5 rounded-lg border border-dash-line text-xs ml-auto">
                <label className="text-dash-mut text-[11px] font-semibold px-1">Date Range:</label>
                <input
                  type="date"
                  value={displayStart}
                  min={dateBounds.min}
                  max={dateBounds.max}
                  onChange={(e) => { setPeriod('CUSTOM'); setCustomStart(e.target.value); }}
                  className="bg-dash-panel border border-dash-line rounded px-2 py-1 text-dash-ink focus:outline-none focus:ring-1 focus:ring-dash-ice"
                  data-testid="custom-start-date"
                />
                <span className="text-dash-mut">to</span>
                <input
                  type="date"
                  value={displayEnd}
                  min={dateBounds.min}
                  max={dateBounds.max}
                  onChange={(e) => { setPeriod('CUSTOM'); setCustomEnd(e.target.value); }}
                  className="bg-dash-panel border border-dash-line rounded px-2 py-1 text-dash-ink focus:outline-none focus:ring-1 focus:ring-dash-ice"
                  data-testid="custom-end-date"
                />
              </div>
            </div>

            {/* Chart */}
            <div className="bg-dash-panel border border-dash-line rounded-xl p-5">
              <h3 className="text-sm font-semibold text-dash-ink mb-0.5">{gradeLabel} — daily rate</h3>
              <p className="text-xs text-dash-mut mb-3">₹ per KG, by supplier, over the selected period.</p>
              <div className="h-[420px]">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                      <CartesianGrid stroke="var(--dash-line)" strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--dash-mut)' }} stroke="var(--dash-line)" minTickGap={40} />
                      <YAxis domain={yDomain} tickFormatter={v => '₹' + v} tick={{ fontSize: 10, fill: 'var(--dash-mut)' }} stroke="var(--dash-line)" label={{ value: 'Rate (Per KG)', angle: -90, position: 'insideLeft', fill: 'var(--dash-mut)', fontSize: 11 }} />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 11, color: 'var(--dash-mut)' }} />
                      {visibleSuppliers.map(s => (
                        <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={2} dot={false} connectNulls={false} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-dash-mut text-sm">No data for current filters.</div>
                )}
              </div>
            </div>

            {/* Summary table */}
            <div className="bg-dash-panel border border-dash-line rounded-xl p-5 overflow-x-auto">
              <h3 className="text-sm font-semibold text-dash-ink mb-3">{gradeLabel} performance summary</h3>
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-dash-panel2 border-b border-dash-line text-[10px] font-semibold text-dash-mut uppercase tracking-wider">
                    <th className="p-2.5">Supplier</th>
                    <th className="p-2.5">Selected Period Average</th>
                    <th className="p-2.5">Selected Min</th>
                    <th className="p-2.5">Selected Max</th>
                    <th className="p-2.5">Latest Price</th>
                    <th className="p-2.5">Trend vs Average</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dash-line text-dash-ink">
                  {visibleSuppliers.map((s) => {
                    const st = stats[s.key];
                    if (!st) {
                      return (
                        <tr key={s.key}>
                          <td className="p-2.5"><span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ background: s.badgeBg, color: s.color }}>{s.label}</span></td>
                          <td className="p-2.5 text-dash-mut" colSpan={5}>N/A</td>
                        </tr>
                      );
                    }
                    const diff = st.latest - st.avg;
                    const pct = st.avg > 0 ? (diff / st.avg * 100) : 0;
                    const color = diff < 0 ? 'var(--dash-green)' : diff > 0 ? 'var(--dash-pink)' : 'var(--dash-amber)';
                    const sign = diff > 0 ? '+' : '';
                    return (
                      <tr key={s.key} className="hover:bg-dash-panel2 transition-colors">
                        <td className="p-2.5"><span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ background: s.badgeBg, color: s.color }}>{s.label}</span></td>
                        <td className="p-2.5 font-mono">₹{fmtNum(st.avg)}</td>
                        <td className="p-2.5 font-mono">₹{fmtNum(st.min)}</td>
                        <td className="p-2.5 font-mono">₹{fmtNum(st.max)}</td>
                        <td className="p-2.5 font-mono">₹{fmtNum(st.latest)}</td>
                        <td className="p-2.5 font-mono font-semibold" style={{ color }}>{sign}{pct.toFixed(1)}% ({sign}{diff.toFixed(2)})</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <FloatingFAB guideContent={GUIDE} />
    </div>
  );
};

export default PriceTrendReact;
